import { useMemo, useState } from 'react'
import { Download, Loader2, RotateCw, X, Youtube } from 'lucide-react'
import { useJob } from '@/api/endpoints/jobs'
import {
  readDownloadSnapshot,
  resolveDownloadUrl,
  useStartYoutubeDownload,
} from '@/api/endpoints/youtubeDownload'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import styles from './YoutubeDownloaderPage.module.scss'

// O lucide deprecou os ícones de marca e vai removê-los na v1.0
// (lucide-icons/lucide#670), recomendando o simple-icons no lugar. Trocar
// agora mudaria o que o usuário vê nesta página, e este card é sobre deixar o
// CI verde, não sobre redesenho — então o alias concentra a dívida num ponto
// só. Quando a troca acontecer, é esta linha que muda.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const YoutubeIcon = Youtube

const YOUTUBE_URL_RE = /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com|youtu\.be)\//i

function formatBytes(bytes: number | undefined): string | null {
  if (!bytes || bytes < 0) return null
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDuration(seconds: number | undefined): string | null {
  if (!seconds || seconds < 0) return null
  const s = Math.floor(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return h > 0 ? `${String(h)}:${pad(m)}:${pad(r)}` : `${String(m)}:${pad(r)}`
}

export function YoutubeDownloaderPage() {
  const [url, setUrl] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const start = useStartYoutubeDownload()
  const job = useJob(jobId)

  const trimmed = url.trim()
  const urlLooksValid = useMemo(() => YOUTUBE_URL_RE.test(trimmed), [trimmed])

  const jobStatus = job.data?.status
  const isRunning = jobStatus === 'pending' || jobStatus === 'running' || start.isPending
  const snapshot = readDownloadSnapshot(job.data)
  const failed = jobStatus === 'failed' || jobStatus === 'cancelled'
  const jobErrorMsg = failed ? (job.data?.error?.['message'] as string | undefined) ?? job.data?.message : null
  // Legacy fallback: jobs started while the backend was still on the
  // ``mode="import"`` default put the resulting VideoProject id inside
  // ``snapshot.stats.video_id`` and never set ``download_url``. Expose
  // it so we can point the user at the created project instead of
  // rendering an empty result section.
  const importedVideoId =
    snapshot && !snapshot.download_url && snapshot.stats?.video_id
      ? snapshot.stats.video_id
      : null

  const handleSubmit = () => {
    if (!urlLooksValid || isRunning) return
    start.mutate(
      { url: trimmed },
      {
        onSuccess: ({ job_id }) => {
          setJobId(job_id)
        },
      },
    )
  }

  const handleReset = () => {
    start.reset()
    setJobId(null)
    setUrl('')
  }

  const startError = start.isError ? errorMessage(start.error, 'Falha ao iniciar download.') : null
  const progress = job.data?.progress ?? (start.isPending ? 2 : 0)
  const progressMsg = job.data?.message ?? (start.isPending ? 'Enviando...' : '')

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Tools"
        title="Baixador de vídeo do YouTube"
        description="Cole a URL de um vídeo do YouTube — o servidor baixa em MP4 (melhor qualidade disponível) e devolve um botão para salvar no seu computador."
      />

      {/* INPUT */}
      <Section title="URL do vídeo" description="Aceita links do youtube.com e youtu.be — vídeos e Shorts.">
        <div className={styles.inputRow}>
          <input
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSubmit()
              }
            }}
            placeholder="https://www.youtube.com/watch?v=..."
            className={styles.input}
            disabled={isRunning}
            aria-label="URL do vídeo do YouTube"
            spellCheck={false}
          />
        </div>
        {url && !urlLooksValid ? (
          <p className={styles.hint}>URL não parece ser um link do YouTube.</p>
        ) : null}

        <div className={styles.submitRow}>
          <Button
            type="button"
            variant="primary"
            leadingIcon={isRunning ? <Loader2 className={styles.spin} /> : <Download />}
            onClick={handleSubmit}
            disabled={!urlLooksValid || isRunning}
          >
            {isRunning ? 'Baixando…' : 'Baixar'}
          </Button>
          {(jobId || url) && !isRunning ? (
            <Button type="button" variant="ghost" leadingIcon={<X />} onClick={handleReset}>
              Limpar
            </Button>
          ) : null}
        </div>

        {startError ? <p className={styles.error}>{startError}</p> : null}
      </Section>

      {/* RESULT */}
      {jobId ? (
        <Section title="Resultado">
          <div role="status" aria-live="polite" className={styles.srOnly}>
            {isRunning ? progressMsg : failed ? `Erro: ${jobErrorMsg ?? 'download falhou'}` : snapshot ? 'Download pronto.' : ''}
          </div>

          {isRunning ? (
            <div className={styles.progressCard}>
              <div className={styles.progressHeader}>
                <YoutubeIcon className={styles.progressIcon} aria-hidden />
                <span className={styles.progressMsg}>{progressMsg || 'Preparando...'}</span>
                <span className={styles.progressPct}>{progress}%</span>
              </div>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${String(progress)}%` }} />
              </div>
            </div>
          ) : failed ? (
            <div className={styles.errorCard}>
              <X className={styles.errorIcon} aria-hidden />
              <div className={styles.errorBody}>
                <strong>Falha ao baixar</strong>
                <p>{jobErrorMsg ?? 'Erro desconhecido.'}</p>
              </div>
              <Button variant="secondary" size="sm" leadingIcon={<RotateCw />} onClick={handleSubmit}>
                Tentar de novo
              </Button>
            </div>
          ) : snapshot?.download_url ? (
            <div className={styles.resultCard}>
              {snapshot.stats?.thumbnail_url ? (
                <img
                  src={snapshot.stats.thumbnail_url}
                  alt=""
                  className={styles.thumb}
                  loading="lazy"
                />
              ) : (
                <div className={styles.thumbFallback} aria-hidden>
                  <YoutubeIcon />
                </div>
              )}
              <div className={styles.resultBody}>
                <h4 className={styles.resultTitle}>{snapshot.stats?.title || 'Vídeo pronto'}</h4>
                <p className={styles.resultMeta}>
                  {[formatDuration(snapshot.stats?.duration), formatBytes(snapshot.stats?.file_size)]
                    .filter(Boolean)
                    .join(' · ') || 'MP4'}
                </p>
              </div>
              <a
                href={resolveDownloadUrl(snapshot.download_url)}
                download={snapshot.suggested_filename ?? 'video.mp4'}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.downloadBtn}
                aria-label="Salvar vídeo no computador"
              >
                <Download aria-hidden />
                <span>Salvar MP4</span>
              </a>
            </div>
          ) : jobStatus === 'succeeded' && importedVideoId ? (
            // Legacy path: the job ran in ``mode="import"`` (server code
            // was still on the old default when the job started) — no
            // ``download_url`` on the snapshot, but a VideoProject was
            // created. Point the user at it and offer a retry that will
            // now hit the new ``mode="download"`` code path.
            <div className={styles.errorCard}>
              <YoutubeIcon className={styles.errorIcon} aria-hidden />
              <div className={styles.errorBody}>
                <strong>Vídeo importado como projeto</strong>
                <p>
                  Foi criado o projeto <code>{importedVideoId}</code>. Rode de novo para baixar
                  o MP4 direto no seu computador em vez de importá-lo.
                </p>
              </div>
              <Button variant="secondary" size="sm" leadingIcon={<RotateCw />} onClick={handleSubmit}>
                Baixar de novo
              </Button>
            </div>
          ) : (
            // We know jobId is set (parent guard) but useJob hasn't come
            // back yet, or the server returned something we don't
            // recognize. Show a neutral placeholder so the section
            // doesn't look empty.
            <div className={styles.progressCard}>
              <div className={styles.progressHeader}>
                <Loader2 className={cn(styles.progressIcon, styles.spin)} aria-hidden />
                <span className={styles.progressMsg}>Carregando estado do job...</span>
              </div>
            </div>
          )}
        </Section>
      ) : null}
    </div>
  )
}
